import type { AnimationMeta } from './schema';
import { versionedAssetUrl } from '../assets/version';
import { frameColumns, frameSrcRect, maxFrameIndex } from './sheetGeometry';

/**
 * Spritesheet drawing (spec.md §N.2). Two implementations behind one interface:
 *  - {@link ImageSpriteSheet} draws frames sliced from a loaded transparent PNG.
 *  - {@link PlaceholderSpriteSheet} draws a clearly-marked procedural stand-in when
 *    no PNG exists yet — so the whole pipeline runs with **zero binary assets** until
 *    final art is dropped in (placeholder-art policy, §N.6).
 *
 * All drawing is **nearest-neighbor** (`imageSmoothingEnabled = false`): no smoothing,
 * no blur (§N.2). The canvas/`Image` code here is browser-only; the frame geometry it
 * relies on is unit-tested in `sheetGeometry.test.ts`.
 */

export interface DrawOptions {
  flipX?: boolean;
  /** 0..1 white hit-flash overlay strength. */
  flashAlpha?: number;
  /** 0..1 overall opacity (e.g., death fade). */
  alpha?: number;
}

export interface SpriteSheet {
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly isPlaceholder: boolean;
  /** Draw `frameIndex` with its top-left at (dx,dy), upscaled by `scale`. */
  draw(ctx: CanvasRenderingContext2D, frameIndex: number, dx: number, dy: number, scale: number, opts?: DrawOptions): void;
}

function withFlip(
  ctx: CanvasRenderingContext2D,
  flipX: boolean,
  dx: number,
  w: number,
  draw: (originX: number) => void,
): void {
  if (!flipX) {
    draw(dx);
    return;
  }
  ctx.save();
  ctx.translate(dx + w, 0);
  ctx.scale(-1, 1);
  draw(0);
  ctx.restore();
}

export class ImageSpriteSheet implements SpriteSheet {
  readonly isPlaceholder = false;
  private readonly columns: number;

  constructor(
    private readonly image: HTMLImageElement | ImageBitmap,
    readonly frameWidth: number,
    readonly frameHeight: number,
  ) {
    this.columns = frameColumns(image.width, frameWidth);
  }

  draw(ctx: CanvasRenderingContext2D, frameIndex: number, dx: number, dy: number, scale: number, opts: DrawOptions = {}): void {
    const { sx, sy, sw, sh } = frameSrcRect(frameIndex, this.frameWidth, this.frameHeight, this.columns);
    const dw = sw * scale;
    const dh = sh * scale;
    ctx.imageSmoothingEnabled = false;
    ctx.save();
    if (opts.alpha !== undefined) ctx.globalAlpha = Math.max(0, Math.min(1, opts.alpha));
    withFlip(ctx, opts.flipX === true, dx, dw, (originX) => {
      ctx.drawImage(this.image, sx, sy, sw, sh, originX, dy, dw, dh);
      if (opts.flashAlpha && opts.flashAlpha > 0) {
        ctx.save();
        ctx.globalCompositeOperation = 'source-atop';
        ctx.globalAlpha = Math.min(1, opts.flashAlpha) * (opts.alpha ?? 1);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(originX, dy, dw, dh);
        ctx.restore();
      }
    });
    ctx.restore();
  }
}

export class PlaceholderSpriteSheet implements SpriteSheet {
  readonly isPlaceholder = true;

  constructor(
    readonly frameWidth: number,
    readonly frameHeight: number,
    private readonly color: string,
    private readonly label: string,
    private readonly frameCount: number,
  ) {}

  draw(ctx: CanvasRenderingContext2D, frameIndex: number, dx: number, dy: number, scale: number, opts: DrawOptions = {}): void {
    const w = this.frameWidth * scale;
    const h = this.frameHeight * scale;
    const i = ((frameIndex % this.frameCount) + this.frameCount) % this.frameCount;
    // A little procedural life so the placeholder visibly animates per frame.
    const bob = Math.round(Math.sin(i * 0.9) * scale * 1.5);
    ctx.imageSmoothingEnabled = false;
    ctx.save();
    if (opts.alpha !== undefined) ctx.globalAlpha = Math.max(0, Math.min(1, opts.alpha));
    withFlip(ctx, opts.flipX === true, dx, w, (originX) => {
      const bodyX = originX + w * 0.2;
      const bodyY = dy + h * 0.18 + bob;
      const bodyW = w * 0.6;
      const bodyH = h * 0.64;
      // body
      ctx.fillStyle = this.color;
      ctx.fillRect(bodyX, bodyY, bodyW, bodyH);
      // outline (clearly a stand-in)
      ctx.strokeStyle = '#00000066';
      ctx.lineWidth = Math.max(1, scale);
      ctx.strokeRect(bodyX, bodyY, bodyW, bodyH);
      // eye marker (shifts with the frame so motion reads)
      ctx.fillStyle = '#0a0908';
      const eye = Math.round((i / Math.max(1, this.frameCount - 1)) * bodyW * 0.5);
      ctx.fillRect(bodyX + bodyW * 0.25 + eye, bodyY + bodyH * 0.25, Math.max(2, scale * 1.5), Math.max(2, scale * 1.5));
      // frame index pip row
      ctx.fillStyle = '#ffffffcc';
      ctx.fillRect(originX + 2, dy + h - Math.max(2, scale * 1.5) - 2, Math.max(2, scale) * (i + 1), Math.max(2, scale));
      // flash overlay
      if (opts.flashAlpha && opts.flashAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = Math.min(1, opts.flashAlpha) * (opts.alpha ?? 1);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(bodyX, bodyY, bodyW, bodyH);
        ctx.restore();
      }
      // tiny label tag
      ctx.fillStyle = '#ffffff99';
      ctx.font = `${Math.max(6, Math.floor(scale * 4))}px ui-monospace, monospace`;
      ctx.textBaseline = 'top';
      ctx.fillText(this.label.slice(0, 3), originX + 2, dy + 2);
    });
    ctx.restore();
  }
}

export interface LoadSpriteSheetOptions {
  meta: AnimationMeta;
  /** PNG URL; when absent or unloadable a placeholder is used. */
  imageUrl?: string;
  /** Placeholder body color. */
  color: string;
  /** Short placeholder label. */
  label: string;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load image: ${url}`));
    // Runtime spritesheets are replaced in place — always load the versioned URL.
    img.src = versionedAssetUrl(url);
  });
}

/**
 * Resolve a {@link SpriteSheet} for an entity. Tries the PNG; on any failure (missing
 * file, decode error) falls back to a procedural placeholder sized from the metadata —
 * so a freshly-added `*.anim.json` renders immediately, before its art exists.
 */
export async function loadSpriteSheet(opts: LoadSpriteSheetOptions): Promise<SpriteSheet> {
  const { meta, imageUrl, color, label } = opts;
  const frameCount = maxFrameIndex(meta) + 1;
  if (imageUrl) {
    try {
      const img = await loadImage(imageUrl);
      return new ImageSpriteSheet(img, meta.frameWidth, meta.frameHeight);
    } catch {
      // fall through to placeholder
      if (typeof console !== 'undefined') {
        console.info(`[anim] no spritesheet at ${imageUrl} — using placeholder for "${label}"`);
      }
    }
  }
  return new PlaceholderSpriteSheet(meta.frameWidth, meta.frameHeight, color, label, frameCount);
}
